// Copyright (c) Docugami, Inc. All rights reserved.

import { StorageProviderAuthenticationModal } from "components/storage/StorageProviderAuthenticationModal";
import { ProviderId} from "networking/StorageProviders";
import { OAuthRequestStatus, Token, UserAuthRequest } from "networking/Authentication";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isTokenExpired, StorageManagerInstance } from "storage/StorageManager";
import { getProviderFriendlyName } from "storage/StorageProviderHelpers";
import { TokenAccessor } from "storage/TokenAccessor";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { useInterval } from "./Interval";
import { isActiveAuthenticationStatus } from "authentication/AuthHelpers";
import { useAppDispatch } from "./redux";

export interface IStorageProviderToken {
    getToken: () => Promise<Token | null>;
    token: Token | null;
    status: OAuthRequestStatus;
    statusModal: JSX.Element | null;
}

export function useStorageProviderToken(id: ProviderId | null, onCancel?: () => void): IStorageProviderToken {
    const [status, setStatus] = useState<OAuthRequestStatus>(OAuthRequestStatus.None);

    const authWindow = useRef<Window | null>(null);
    const tokenAccessor = useRef<TokenAccessor | null>(!!id ? new TokenAccessor(id, StorageManagerInstance) : null);

    const { execute: executeGetToken, state: getTokenStatus, data: token } = useAsyncHelper<Token | null>("Get Token", id);
    const dispatch = useAppDispatch();
    useInterval(ensureToken, !!token ? 5000 : undefined);

    const openAuthWindow = useCallback((authRequest: UserAuthRequest, id: ProviderId) => {
        authWindow.current?.close();

        const windowFeatures = 'toolbar=no, menubar=no, width=500, height=600, top=0, left=0';
        const windowName = `Connect ${getProviderFriendlyName(id)} to Docugami`;
        authWindow.current = window.open(authRequest.address, windowName, windowFeatures);
    }, []);

    useEffect(() => {
        if (!id) {
            return;
        }

        tokenAccessor.current = new TokenAccessor(id, StorageManagerInstance);

        const unregister1 = tokenAccessor.current.onUserAuthRequested.addListener(authRequest => openAuthWindow(authRequest, id));
        const unregister3 = tokenAccessor.current.onStatusChanged.addListener(s => setStatus(s));

        return () => {
            unregister1();
            unregister3();

            authWindow.current?.close();
            tokenAccessor.current?.cancel();
            tokenAccessor.current = null;
        }
    }, [id, dispatch, openAuthWindow]);

    useEffect(() => {
        if (!isActiveAuthenticationStatus(status) || status === OAuthRequestStatus.Authorizing) {
            authWindow.current?.close();
        }
    }, [status]);

    const getToken = useCallback(async (): Promise<Token | null> => {
        if (getTokenStatus === RequestState.InProgress) {
            return null;
        }

        const promise = tokenAccessor.current!.getToken();
        executeGetToken(promise);
        return promise;
    }, [executeGetToken, getTokenStatus]);

    const onModalDismiss = useCallback(() => {
        tokenAccessor.current?.cancel();
        onCancel?.()
    }, [onCancel])

    const statusModal = useMemo((): JSX.Element | null => {
        return !!id ? <StorageProviderAuthenticationModal
            providerId={id}
            status={status}
            onCancel={onModalDismiss}
            onDismiss={onModalDismiss} /> :
            null
    }, [id, status, onModalDismiss]);

    function ensureToken() {
        if (!!token && isTokenExpired(token, 60) && getTokenStatus !== RequestState.InProgress) {
            // Token is expiring in less than a minute - let's try to refresh
            getToken();
        }
    }

    return {
        token: token ?? null,
        status: status,
        getToken: getToken,
        statusModal: statusModal
    };
}
